import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import { useAuth, useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import type { FilterDefinition } from "@/shared/components/ResourceView";
import { useCustomFieldFilterDefinitions } from "@/shared/components/ResourceView/customFields";
import {
  deviceSessionsQuery,
  useCoreSessionFilters,
  useSessionCrossIdFilters,
  useSessionFilterDefinitions,
} from "@/shared/components/sessions";
import {
  activityOf,
  Timeline,
  type FetchItems,
  type GetActivity,
  itemsOverlapping,
  type TimelineTrackItem,
} from "@/shared/components/Timeline";
import { TargetEntityType } from "@/shared/domain/customFields";
import {
  allQueryResultsOptions,
  structuredQueryOptions,
  useAllQueryResults,
  useQueryCount,
  useStructuredQuery,
  type UseStructuredQueryOptions,
} from "@/shared/domain/query/hooks";
import type { SessionRecord } from "@/shared/domain/sessions/SessionRecord";

import { useDeviceContext } from "../Device/useDeviceContext";

import styles from "./DeviceOverview.module.css";
import { NoSessions } from "./NoSessions";
import { OverviewFilterBar } from "./OverviewFilterBar";
import type { OverviewFilters } from "./overviewUrl";
import { SessionDetailDock } from "./SessionDetailDock";
import { toSessionTracks } from "./sessionTracks";
import { UntimedSessionsTray } from "./UntimedSessionsTray";
import { useOverviewUrlState } from "./useOverviewUrlState";

/**
 * The Overview tab: the device's sessions laid out in time.
 *
 * Loading and failure are the timeline's own: it shows a skeleton while
 * `getActivity` is pending and an error with Retry if it rejects. With no
 * timed session it shows `NoSessions` in its place.
 */
export function DeviceOverview() {
  const { deviceId } = useDeviceContext();
  const url = useOverviewUrlState();
  const { definitions, submitOptions } = useSessionQuery(deviceId, url.filters);
  const { items, untimed, total, getData } = useSessionResults(submitOptions);
  const getActivity = useSessionActivity(submitOptions);
  const selectedItem = useSelectedItem(url.session, items);

  return (
    <div className={styles.overview}>
      <OverviewFilterBar
        filters={url.filters}
        definitions={definitions}
        onChange={url.updateFilters}
      />
      <Timeline<SessionRecord>
        getData={getData}
        getActivity={getActivity}
        totalCount={total}
        tz={url.tz}
        onTzChange={url.setTz}
        initialTimeWindow={url.initialTimeWindow}
        onTimeWindowChange={url.setTimeWindow}
        selectedItem={selectedItem}
        onSelect={(item) => url.setSession(item?.id ?? null)}
        hotkeys
        empty={
          <NoSessions
            filtered={url.filters.filters.length > 0}
            untimedCount={untimed.length}
            onClearFilters={() =>
              url.updateFilters((previous) => ({ ...previous, filters: [] }))
            }
          />
        }
      />
      {selectedItem && (
        <SessionDetailDock
          session={selectedItem.data}
          deviceId={deviceId}
          tz={url.tz}
        />
      )}
      {untimed.length > 0 && (
        <UntimedSessionsTray
          sessions={untimed}
          tz={url.tz}
          // Open from the start when nothing else is on the timeline.
          defaultOpen={items.length === 0}
        />
      )}
    </div>
  );
}

/**
 * The query for the device's sessions under the user's `filters`, and the
 * filter rows the bar offers. Built from the Sessions tab's own scope, filter
 * rows and query builder, so for the same filters the timeline shows what its
 * table lists. The scope already fixes the device, so dataset id is the only
 * other entity's id offered as a filter.
 *
 * `filters` must keep its identity until the filters themselves change (as
 * `useOverviewUrlState` keeps it), so a pan or a selection does not build a
 * new query.
 */
function useSessionQuery(
  deviceId: string,
  filters: OverviewFilters,
): {
  definitions: FilterDefinition[];
  submitOptions: UseStructuredQueryOptions;
} {
  const { currentOrganization } = useAuth();
  const resolvedOrgId = useCurrentOrgId(currentOrganization?.org_id);

  const coreFilters = useCoreSessionFilters(resolvedOrgId);
  const crossIdFilters = useSessionCrossIdFilters(resolvedOrgId);
  const mountDefinitions = React.useMemo<FilterDefinition[]>(
    () => [...coreFilters, crossIdFilters.datasetIdFilter],
    [coreFilters, crossIdFilters],
  );
  const customFieldDefinitions = useCustomFieldFilterDefinitions(
    TargetEntityType.Session,
  );
  const definitions =
    useSessionFilterDefinitions(
      resolvedOrgId,
      mountDefinitions,
      customFieldDefinitions,
    ) ?? mountDefinitions;

  const submitOptions = React.useMemo(
    () =>
      deviceSessionsQuery({
        orgId: resolvedOrgId,
        deviceId,
        filters,
        definitions,
      }),
    [resolvedOrgId, deviceId, filters, definitions],
  );

  return { definitions, submitOptions };
}

/**
 * The sessions `submitOptions` selects, split into what the timeline can
 * place (`items`) and what it cannot (`untimed`).
 *
 * - `total` is every session matching the filters, loaded or not; while the
 *   count is unknown, the loaded sessions stand in.
 * - `getData` answers from memory, since everything is already loaded: a
 *   window's items are a filter over `items`. It changes identity only when a
 *   new result set arrives, so the timeline keeps the previous tracks while
 *   new filters load.
 */
function useSessionResults(submitOptions: UseStructuredQueryOptions) {
  const submit = useStructuredQuery(submitOptions);
  const queryId = submit.data?.query_id;
  const all = useAllQueryResults<SessionRecord>(queryId);
  const count = useQueryCount(queryId);

  const sessions = all.data?.items;
  const { items, untimed } = React.useMemo(
    () => toSessionTracks(sessions ?? []),
    [sessions],
  );
  const getData = React.useCallback<FetchItems<SessionRecord>>(
    (window) => Promise.resolve(itemsOverlapping(items, window)),
    [items],
  );

  return {
    items,
    untimed,
    total: count.data ?? sessions?.length ?? 0,
    getData,
  };
}

/**
 * The timeline's `getActivity` for the sessions `submitOptions` selects: the
 * whole history, bucketed client-side. It changes identity as soon as the
 * filters do and resolves only once their sessions arrive, through the same
 * cache entries `useStructuredQuery` and `useAllQueryResults` read, so nothing
 * is fetched twice; the timeline dims the previous strip and tracks until
 * then. "Now" is captured once per result set, here, so the extent's right edge
 * and the opening window agree.
 */
function useSessionActivity(
  submitOptions: UseStructuredQueryOptions,
): GetActivity {
  const queryClient = useQueryClient();
  const { queryService } = useDomainServices();
  return React.useCallback<GetActivity>(
    async ({ bucketCount }) => {
      const { query_id } = await queryClient.ensureQueryData(
        structuredQueryOptions(queryService, submitOptions),
      );
      const loaded = await queryClient.ensureQueryData(
        allQueryResultsOptions<SessionRecord>(queryService, query_id),
      );
      return activityOf(
        toSessionTracks(loaded.items).items,
        bucketCount,
        Date.now(),
      );
    },
    [queryClient, queryService, submitOptions],
  );
}

/**
 * The selected session's item on the timeline.
 */
function useSelectedItem(
  sessionId: string | null,
  items: readonly TimelineTrackItem<SessionRecord>[],
): TimelineTrackItem<SessionRecord> | null {
  return React.useMemo(
    () =>
      sessionId === null
        ? null
        : items.find((item) => item.id === sessionId) ?? null,
    [sessionId, items],
  );
}
